import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// INTERFACES
// ================================

export interface LoyaltyTierConfig {
  _id: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  name: {
    en: string;
    th?: string;
    zh?: string;
  };
  minLifetimePoints: number;
  minLifetimeSpend?: number;
  minOrdersCount?: number;
  earnRate: number;
  earnMultiplier: number;
  redeemRate: number;
  birthdayBonus: number;
  birthdayMultiplier: number;
  welcomeBonus: number;
  monthlyBonus?: number;
  discountPercentage?: number;
  freeUpgradeCount: number;
  prioritySupport: boolean;
  exclusivePromotions: boolean;
  earlyAccess: boolean;
  freeDelivery: boolean;
  birthdayFreeItem: boolean;
  pointsExpirationMonths: number;
  gracePeriodonths: number;
  color?: string;
  iconUrl?: string;
  badgeUrl?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyMember {
  _id: string;
  memberId: string;
  phone: string;
  email?: string;
  name: string;
  nickname?: string;
  birthdate?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';

  // Points
  currentPoints: number;
  lifetimePoints: number;
  lifetimeSpend: number;

  // Tier
  tierId?: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  tierAchievedAt?: string;
  tierExpiresAt?: string;
  previousTier?: string;

  // Activity stats
  totalOrders: number;
  lastOrderAt?: string;
  lastVisitBranchId?: string;
  favoriteItems: string[];

  // Registration
  registrationSource: 'pos_scan' | 'mobile_app' | 'web' | 'referral' | 'import';
  registeredAt: string;
  registeredBranchId?: string;

  // Referral
  referralCode: string;
  referredBy?: string;
  referralCount: number;
  referralPointsEarned: number;

  // Birthday tracking
  birthdayRewardClaimed: boolean;
  birthdayRewardYear?: number;
  birthdayRewardClaimedAt?: string;

  // Communication preferences
  smsOptIn: boolean;
  emailOptIn: boolean;
  pushOptIn: boolean;
  language: string;

  // Verification
  phoneVerified: boolean;
  emailVerified: boolean;
  verifiedAt?: string;

  // Status
  isActive: boolean;
  isBanned: boolean;
  banReason?: string;

  notes?: string;
  tags: string[];

  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyTransaction {
  _id: string;
  memberId: string;
  customerId: string;
  type:
    | 'earn'
    | 'redeem'
    | 'expire'
    | 'adjustment'
    | 'bonus'
    | 'welcome'
    | 'referral_earn'
    | 'referral_bonus'
    | 'tier_bonus'
    | 'birthday'
    | 'monthly'
    | 'reversal';
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  orderId?: string;
  branchId?: string;
  description: string;
  metadata?: {
    orderAmount?: number;
    earnRate?: number;
    multiplier?: number;
    tierAtTime?: string;
    promoId?: string;
    referralMemberId?: string;
    adjustmentReason?: string;
    adjustedBy?: string;
    originalTransactionId?: string;
  };
  expiresAt?: string;
  isExpired: boolean;
  expiredAt?: string;
  isReversed: boolean;
  reversedAt?: string;
  reversedBy?: string;
  reversalReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyAnalyticsOverview {
  totalMembers: number;
  activeMembers: number;
  tierDistribution: Record<string, number>;
  totalPointsIssued: number;
  totalPointsRedeemed: number;
  recentSignups: number;
  engagementRate: string;
}

export interface LoyaltyTrends {
  signupTrend: Array<{ _id: string; count: number }>;
  pointsTrend: Array<{ _id: string; points: number; count: number }>;
  redemptionTrend: Array<{ _id: string; points: number; count: number }>;
  period: {
    days: number;
    startDate: string;
    endDate: string;
  };
}

export interface ReferralStats {
  referralCode: string;
  totalReferrals: number;
  totalPointsEarned: number;
  referredMembers: Array<{
    name: string;
    tier: string;
    registeredAt: string;
  }>;
  recentEarnings: LoyaltyTransaction[];
}

export interface ReferralAnalytics {
  totalReferrals: number;
  topReferrers: Array<{
    memberId: string;
    name: string;
    referralCount: number;
    referralPointsEarned: number;
  }>;
  conversionRate: string;
}

// ================================
// MEMBER QUERIES
// ================================

/**
 * Search members by phone, memberId, or name
 */
export function useSearchMembers(query: string) {
  return useQuery({
    queryKey: ['loyalty', 'search', query],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ members: LoyaltyMember[] }>>(
        `/loyalty/search?q=${encodeURIComponent(query)}`
      );
      return response.data.data.members;
    },
    enabled: query.length >= 3,
  });
}

/**
 * Get all members with pagination and filtering (admin)
 */
export function useMembers(options?: {
  page?: number;
  limit?: number;
  tier?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) {
  const { page = 1, limit = 50, tier, isActive, sortBy = 'createdAt', sortOrder = 'desc' } = options || {};

  return useQuery({
    queryKey: ['loyalty', 'members', { page, limit, tier, isActive, sortBy, sortOrder }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);
      if (tier) params.append('tier', tier);
      if (isActive !== undefined) params.append('isActive', isActive.toString());

      const response = await apiClient.get<
        APIResponse<{
          members: LoyaltyMember[];
          pagination: { page: number; limit: number; total: number; pages: number };
        }>
      >(`/loyalty/members?${params.toString()}`);
      return response.data.data;
    },
  });
}

/**
 * Get a single member by ID, phone, or memberId
 */
export function useMember(identifier?: string) {
  return useQuery({
    queryKey: ['loyalty', 'member', identifier],
    queryFn: async () => {
      const response = await apiClient.get<
        APIResponse<{
          member: LoyaltyMember;
          tierConfig: LoyaltyTierConfig | null;
          nextTierConfig: LoyaltyTierConfig | null;
          transactions: LoyaltyTransaction[];
          canClaimBirthday: boolean;
          pointsToNextTier: number | null;
        }>
      >(`/loyalty/member/${identifier}`);
      return response.data.data;
    },
    enabled: !!identifier && identifier.length > 0,
  });
}

/**
 * Get member by phone or ID (legacy compatibility)
 */
export function useCustomer(identifier?: string) {
  return useQuery({
    queryKey: ['loyalty', 'customer', identifier],
    queryFn: async () => {
      const response = await apiClient.get<
        APIResponse<{
          customer: LoyaltyMember;
          transactions: LoyaltyTransaction[];
          tierConfig: LoyaltyTierConfig | null;
        }>
      >(`/loyalty/${identifier}`);
      return response.data.data;
    },
    enabled: !!identifier && identifier.length > 0,
  });
}

// ================================
// TRANSACTION QUERIES
// ================================

/**
 * Get transaction history for a member
 */
export function useTransactions(
  memberId?: string,
  options?: { page?: number; limit?: number; type?: string }
) {
  const { page = 1, limit = 20, type } = options || {};

  return useQuery({
    queryKey: ['loyalty', 'transactions', memberId, { page, limit, type }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (type) params.append('type', type);

      const response = await apiClient.get<
        APIResponse<{
          transactions: LoyaltyTransaction[];
          pagination: { page: number; limit: number; total: number; pages: number };
        }>
      >(`/loyalty/transactions/${memberId}?${params.toString()}`);
      return response.data.data;
    },
    enabled: !!memberId,
  });
}

// ================================
// TIER QUERIES
// ================================

/**
 * Get all tier configurations
 */
export function useTiers() {
  return useQuery({
    queryKey: ['loyalty', 'tiers'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ tiers: LoyaltyTierConfig[] }>>('/loyalty/tiers');
      return response.data.data.tiers;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ================================
// BIRTHDAY QUERIES
// ================================

/**
 * Get members eligible for birthday rewards
 */
export function useBirthdayEligibleMembers() {
  return useQuery({
    queryKey: ['loyalty', 'birthday', 'eligible'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ members: LoyaltyMember[] }>>(
        '/loyalty/birthday/eligible'
      );
      return response.data.data.members;
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });
}

// ================================
// REFERRAL QUERIES
// ================================

/**
 * Validate a referral code
 */
export function useValidateReferralCode(code?: string) {
  return useQuery({
    queryKey: ['loyalty', 'referral', 'validate', code],
    queryFn: async () => {
      const response = await apiClient.get<
        APIResponse<{
          valid: boolean;
          referrerName: string;
          referrerTier: string;
        }>
      >(`/loyalty/referral/${code}`);
      return response.data.data;
    },
    enabled: !!code && code.length >= 6,
  });
}

/**
 * Get referral stats for a member
 */
export function useReferralStats(memberId?: string) {
  return useQuery({
    queryKey: ['loyalty', 'referral', 'stats', memberId],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<ReferralStats>>(
        `/loyalty/referral/stats/${memberId}`
      );
      return response.data.data;
    },
    enabled: !!memberId,
  });
}

// ================================
// ANALYTICS QUERIES
// ================================

/**
 * Get loyalty program overview (admin)
 */
export function useLoyaltyAnalyticsOverview() {
  return useQuery({
    queryKey: ['loyalty', 'analytics', 'overview'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<LoyaltyAnalyticsOverview>>(
        '/loyalty/analytics/overview'
      );
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Get loyalty trends over time (admin)
 */
export function useLoyaltyTrends(days: number = 30) {
  return useQuery({
    queryKey: ['loyalty', 'analytics', 'trends', days],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<LoyaltyTrends>>(
        `/loyalty/analytics/trends?days=${days}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get referral program analytics (admin)
 */
export function useReferralAnalytics() {
  return useQuery({
    queryKey: ['loyalty', 'analytics', 'referrals'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<ReferralAnalytics>>(
        '/loyalty/analytics/referrals'
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ================================
// LEGACY EXPORTS (backward compatibility)
// ================================

export type Customer = LoyaltyMember;

export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ['loyalty', 'customers', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('q', search);

      const response = await apiClient.get<APIResponse<{ members: LoyaltyMember[] }>>(
        `/loyalty/search?${params.toString()}`
      );
      return response.data.data.members;
    },
    enabled: !search || search.length >= 3,
  });
}

export function useCustomerTransactions(customerId?: string) {
  return useTransactions(customerId);
}
